import { Box } from "@mui/material";
import { type FC, useCallback, useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useOneTimeNotice } from "@/ui/molecules/OneTimeNotice";
import SystemDialog from "@/ui/Popups/Dialog";
import { GAPS_WHATS_NEW_NOTICE } from "./gapsWhatsNewCampaign";

type Props = {
	departmentId: string;
};

/**
 * The one-time "what's new" popup on the gaps-first home: this is a new layout, nothing else changed, and the
 * classic view is one tap away under More.
 *
 * A dialog rather than an inline note because people left the inline note on screen instead of reading it
 * (APP-886): a popup has to be answered before the screen can be used. Any way out of it (Got it, the backdrop,
 * Escape) counts as read, so the user is never asked twice. The eligibility also gates the `gaps_whats_new_shown`
 * impression, which is what makes "Got it" and "Back to classic view" measurable against how many people saw it.
 * The menu labels are the menu's own keys, so the text can never name a row the More menu does not have.
 */
export const GapsWhatsNewNotice: FC<Props> = ({ departmentId }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const handleDismiss = useCallback(() => track("gaps_whats_new_dismissed", { department_id: departmentId }), [track, departmentId]);
	const notice = useOneTimeNotice(GAPS_WHATS_NEW_NOTICE.key, {
		expiresAt: GAPS_WHATS_NEW_NOTICE.expiresAt,
		// DEFAULT: no `onlyForAccountsCreatedBefore` — every account is told, new ones included; see
		// GAPS_WHATS_NEW_NOTICE for why.
		onDismiss: handleDismiss,
	});

	// Once per mount (the home remounts per department), not per render: `track`'s identity can turn over
	// when an analytics provider re-renders, which would otherwise count one view several times.
	const hasTrackedShownRef = useRef(false);
	useEffect(() => {
		if (!notice.isEligible || hasTrackedShownRef.current) return;
		hasTrackedShownRef.current = true;
		track("gaps_whats_new_shown", { department_id: departmentId });
	}, [notice.isEligible, track, departmentId]);

	return (
		<SystemDialog
			open={notice.isEligible}
			onClose={notice.dismiss}
			onConfirm={notice.dismiss}
			title={t("gaps.whatsNew.title")}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
					<span>{t("gaps.whatsNew.message")}</span>
					<span>
						<Trans
							i18nKey="gaps.whatsNew.classicHint"
							values={{ more: t("gaps.nav.more"), classic: t("gaps.more.backToClassic") }}
							// The two labels are what the user has to find on screen, so they stand out from the sentence.
							components={{ b: <Box component="span" sx={{ fontWeight: 600 }} /> }}
						/>
					</span>
				</Box>
			}
			confirm={t("common.gotIt")}
			confirmButtonVariant="primary"
			hideCancel
			dataTestId="gaps-whats-new"
		/>
	);
};
